import React from "react";
import { Html } from "react-konva-utils";
import { Floater } from "frontend/ui-components/floaters/floater";
import BoundingBox from "frontend/geometry/bounding-box";
import { ColorPicker } from "frontend/canvas-designer-new/elements-toolbar/widgets/color-picker";
import { ColorPicker as ColorPickerCim } from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/widgets/color-picker";
import type { IGanttSplitCellController } from "elements/gantt/controllers/split-cell-controller";
import styles from "./split-cell-toolbar.module.css";
import { TrashIcon } from "frontend/icons/trash-icon";
import { TasksColors } from "elements/gantt/constants";
import cn from "classnames";
import { GanttAddGroupIcon } from "elements/gantt/icons/gantt-add-group-icon";
import { GanttAddSubGroupIcon } from "elements/gantt/icons/gantt-add-sub-group-icon";
import { Tooltip } from "frontend/ui-components/floaters/floaters";
import { trackGanttEvent } from "elements/gantt/utils";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";

type SplitCellToolbarProps = {
  cellBbox: BoundingBox;
  controller?: IGanttSplitCellController;
};

const Seperator = () => <div className={styles.seperator} />;

export const SplitCellToolbar = ({ cellBbox, controller }: SplitCellToolbarProps) => {
  const isCIM = useIsCIMReorderEnabled();
  const newFloaters = useFeatureFlag("cim-new-floaters-style");
  const newStyle = isCIM && newFloaters;

  return (
    <Html transform={false}>
      <Floater
        relativeTo={cellBbox}
        arrowSize={0}
        margin={10}
        boundary="no-controls-area"
        className={newStyle ? styles.wrapperCIM : styles.wrapper}
      >
        <div className={styles.toolbarActions}>
          <Tooltip label={"Add Row"} side={"top"} tooltipClassName={styles.tooltip}>
            <div
              className={cn(styles.remove, styles.action)}
              onClick={() => {
                controller?.addRow();
                trackGanttEvent("gantt_row_added", {
                  from: "column_toolbar",
                });
              }}
            >
              <GanttAddGroupIcon />
            </div>
          </Tooltip>

          <Seperator />

          <Tooltip label={"Add Column"} side={"top"} tooltipClassName={styles.tooltip}>
            <div
              className={cn(styles.remove, styles.action)}
              onClick={() => {
                controller?.addColumn();
                trackGanttEvent("gantt_column_added", {
                  from: "column_toolbar",
                });
              }}
              data-testid="gantt-split-cell-toolbar-add-column"
            >
              <GanttAddSubGroupIcon />
            </div>
          </Tooltip>

          {!controller?.isCustomRowSplitMode() && (
            <>
              <Seperator />
              {newStyle ? (
                <ColorPickerCim
                  colorPalette={TasksColors}
                  value={controller?.getColor()}
                  multi={false}
                  onChange={(color) => controller?.changeColor(color.slice(0, 7))}
                  isSplitCell={true}
                  margin={16}
                />
              ) : (
                <ColorPicker
                  colorPalette={TasksColors}
                  value={controller?.getColor()}
                  onChange={(color) => controller?.changeColor(color.slice(0, 7))}
                  isSplitCell={true}
                />
              )}
            </>
          )}

          {controller?.canDelete() && (
            <>
              <Seperator />
              <div
                className={cn(styles.remove, styles.action)}
                onClick={() => {
                  controller.deleteRow();

                  trackGanttEvent("gantt_row_deleted", {
                    from: "column_toolbar",
                  });
                }}
                data-testid="gantt-split-cell-toolbar-delete-row"
              >
                <TrashIcon size={20} color="#FEFEFE" />
              </div>
            </>
          )}
        </div>
      </Floater>
    </Html>
  );
};
